import { pipe } from "@mobily/ts-belt"
import type {
  CardAttachment,
  CardColorType,
  CardGoogleClassroom,
  CardStack,
  CardStandards,
  CardTitleValueHtml,
  CardValueHtml,
  LegacyCardStack,
  LessonCard,
  UnifiedCardStack,
} from "@shared/models/card-stack"
import {
  headlessCardStackSchema,
  headlessCardValueExtensions,
} from "@shared/unified-tiptap/extensions"
import { objectId } from "@shared/util/object-id"
import type { ExtensionCardGoogleClassroomAttrs } from "@tiptap-content/extensions/card-google-classroom/card-google-classroom-content-extension"
import type { ExtensionCardTitleAttrs } from "@tiptap-content/extensions/card-title/card-title-content-extension"
import type { CardExtensionAttrs } from "@tiptap-content/extensions/cards/card-content-extensions"
import type { ExtensionStandardAttrs } from "@tiptap-content/extensions/standard/standard-content-extension"
import type { JSONContent } from "@tiptap/core"
import { generateJSON } from "@tiptap/core"
import { DOMSerializer, Fragment, Node as PMNode, Schema } from "@tiptap/pm/model"
import { cloneDeep, sortBy } from "lodash-es"
import { JoinListDOMSerializer } from "tiptap-extension-flat-list"
import { addTrailingBreaks } from "tiptap-extension-trailing-break"
import { emptyCardStackExtracted, extractFromProseMirrorDoc } from "./extracted"
import { initialIdList } from "./prose-mirror-utils"

export function isLegacyCardStack(cardStack: CardStack): cardStack is LegacyCardStack {
  return cardStack.attributes.schemaVersion !== 2
}

export function isUnifiedCardStack(cardStack: CardStack): cardStack is UnifiedCardStack {
  return cardStack.attributes.schemaVersion === 2
}

/**
 * Returns a UnifiedCardStack copy of the input, migrated if needed.
 *
 * The copy should be treated as **readonly** in that:
 * 1. It does not automatically reflect updates to the input cardStack.
 * 2. Patches applied to the copy may not make sense when applied to the original
 * in the DB, if the copy was migrated.
 */
export function readonlyUnifiedCopy(
  cardStack: CardStack,
  onMigrationError?: (error: unknown, card: LessonCard) => void,
): UnifiedCardStack {
  if (isUnifiedCardStack(cardStack)) return cloneDeep(cardStack)
  if (
    !(
      cardStack.attributes.usedAs === "lesson" ||
      cardStack.attributes.usedAs === "lesson-template" ||
      cardStack.attributes.usedAs === "unit" ||
      // This isn't allowed by the types, but there are a lot of "template" CardStacks in the prod DB,
      // including some of the gallery templates. Treat as lesson-template.
      // @ts-expect-error
      cardStack.attributes.usedAs === "template" ||
      // Protect against null fields from missing permissions.
      // This can happen if it's a school template you can't access, used in
      // a planbook that you can access.
      !cardStack.attributes.usedAs
    )
  ) {
    throw new Error(
      `Cannot view ${cardStack.id} as a UnifiedCardStack: usedAs = "${cardStack.attributes.usedAs}"`,
    )
  }

  const migrated = cloneDeep(cardStack) as UnifiedCardStack
  migrated.attributes.schemaVersion = 2

  // Protect against null fields from missing permissions.
  migrated.attributes.commentIds = [...(cardStack.attributes.commentIds ?? [])]

  if (migrated.attributes.cards) {
    const doc = cardStackDocFromLegacyCards(migrated.attributes.cards ?? [], onMigrationError)
    migrated.attributes.proseMirror = { json: doc.toJSON(), ids: initialIdList(doc).save() }
    migrated.attributes.extracted = extractFromProseMirrorDoc(doc)
  } else {
    migrated.attributes.proseMirror = null
    migrated.attributes.extracted = emptyCardStackExtracted()
  }

  return migrated
}

/**
 * Returns a LegacyCardStack copy of the input, migrated if needed.
 *
 * The copy should be treated as **readonly** in that:
 * 1. It does not automatically reflect updates to the input cardStack.
 * 2. Patches applied to the copy may not make sense when applied to the original
 * in the DB, if the copy was migrated.
 */
export function readonlyLegacyCopy(cardStack: CardStack): LegacyCardStack {
  if (isLegacyCardStack(cardStack)) return cloneDeep(cardStack)

  const migrated = cloneDeep(cardStack) as LegacyCardStack
  migrated.attributes.schemaVersion = 1
  migrated.attributes.proseMirror = null
  migrated.attributes.extracted = null

  return migrated
}

/**
 * Given a CardStack loaded from the DB that has `migrateFromCards: true`,
 * complete the migration in-place, setting `cardStack.attributes.proseMirror`
 * as a function of the current cards.
 *
 * See Spikey.CardStack.OneOff.migrate_to_unified.
 */
export function migrateFromCardsInPlace(
  cardStack: CardStack,
  onError?: (error: unknown, card: LessonCard) => void,
): void {
  // Partial documents (relationship/meta-only sideloads whose attributes arrive later)
  // legitimately pass through the store insert hooks; there is nothing to migrate yet.
  if (cardStack.attributes === undefined) return
  if (
    cardStack.attributes.schemaVersion === 2 &&
    // @ts-expect-error migrateFromCards is only on the backend type
    cardStack.attributes.proseMirror?.["migrateFromCards"]
  ) {
    // Complete the migration from Spikey.CardStack.OneOff.migrate_to_unified.
    if (cardStack.attributes.cards === null) {
      cardStack.attributes.proseMirror = null
    } else {
      const doc = cardStackDocFromLegacyCards(cardStack.attributes.cards, onError)
      const idList = initialIdList(doc)
      cardStack.attributes.proseMirror = { json: doc.toJSON(), ids: idList.save() }
    }
  }
}

// ----------
// cardStackDocFromLegacyCards
// ----------

/**
 * Converts a CardStack's legacy cards to a ProseMirror doc node.
 */
export function cardStackDocFromLegacyCards(
  cards: Array<LessonCard>,
  onError?: (error: unknown, card: LessonCard) => void,
): PMNode {
  const schema = headlessCardStackSchema()
  const sortedCards = sortBy(cards, "attributes.position")
  const cardNodes = sortedCards.map((card) => {
    try {
      return convertCard(schema, card)
    } catch (error) {
      console.error("Failed to convert legacy card to ProseMirror node", card.id, error)
      onError?.(error, card)
      return blankCard(
        schema,
        "title" in card.attributes ? card.attributes.title : "",
        card.attributes.color,
        card.attributes.isPublic,
      )
    }
  })

  return schema.node("doc", null, cardNodes)
}

function convertCard(schema: Schema, card: LessonCard): PMNode {
  switch (card.type) {
    case "card-title-value-html":
      return convertCardTitleValueHtml(schema, card, card.attributes.title)
    case "card-value-html":
      return convertCardValueHtml(schema, card)
    case "card-standards":
      return convertCardStandards(schema, card)
    case "card-google-classroom":
      return convertCardGoogleClassroom(schema, card)
  }
}

function convertCardTitleValueHtml(
  schema: Schema,
  card: CardTitleValueHtml,
  title: string,
): PMNode {
  const preprocessed = preprocessHtml(card.attributes.value)
  const doc = generateJSON(preprocessed, headlessCardValueExtensions())
  const blocksJson = doc.content as JSONContent[]
  const blocks =
    blocksJson.length > 0
      ? blocksJson.map((block) => schema.nodeFromJSON(block))
      : [schema.node("paragraph")]
  return schema.node(
    "titledCard",
    {
      id: card.id,
      parentCardId: card.attributes.parentCardId,
      commentIds: card.attributes.commentIds,
      isPublic: card.attributes.isPublic,
    } satisfies CardExtensionAttrs,
    [
      cardTitleNode(schema, title, card.attributes.color),
      ...blocks,
      ...attachmentNodes(schema, card.attributes.attachments),
    ],
  )
}

function convertCardValueHtml(schema: Schema, card: CardValueHtml): PMNode {
  const preprocessed = preprocessHtml(card.attributes.value)
  const doc = generateJSON(preprocessed, headlessCardValueExtensions())
  const blocksJson = doc.content as JSONContent[]
  const blocks =
    blocksJson.length > 0
      ? blocksJson.map((block) => schema.nodeFromJSON(block))
      : [schema.node("paragraph")]
  return schema.node(
    "untitledCard",
    {
      id: card.id,
      parentCardId: card.attributes.parentCardId,
      commentIds: card.attributes.commentIds,
      isPublic: card.attributes.isPublic,
    } satisfies CardExtensionAttrs,
    [...blocks, ...attachmentNodes(schema, card.attributes.attachments)],
  )
}

/**
 * Converts a card's legacy attachments to linkBlock tokens at the card's bottom
 * (docs/unified-links.md §10): the legacy `id` becomes `attachmentId` (same value — it
 * keys `attachmentSettings`), the other fields map 1:1, and `source` gets no backfill
 * (this editor never shipped, so there is no real attachment data to preserve).
 */
function attachmentNodes(schema: Schema, attachments: Array<CardAttachment>): PMNode[] {
  return attachments.map((attachment) =>
    schema.node("linkBlock", {
      attachmentId: attachment.id,
      title: attachment.title,
      url: attachment.url,
      type: attachment.type,
      mimetype: attachment.mimetype,
      size: attachment.size,
      isWriteable: attachment.isWriteable,
      renderAs: "block",
    }),
  )
}

function convertCardStandards(schema: Schema, card: CardStandards): PMNode {
  const { title, standards } = card.attributes

  const standardNodes = standards.map((s) =>
    schema.node("standard", {
      standard: {
        id: s.id,
        code: s.code,
        statement: s.statement,
        jurisdiction: s.jurisdiction,
        grades: s.grades,
        standardSetId: s.standardSetId,
        subject: s.subject,
      },
      emphasisLevel: s.emphasisLevel,
    } satisfies ExtensionStandardAttrs),
  )

  return schema.node(
    "titledCard",
    {
      id: card.id,
      parentCardId: card.attributes.parentCardId,
      commentIds: card.attributes.commentIds,
      isPublic: card.attributes.isPublic,
    } satisfies CardExtensionAttrs,
    [
      cardTitleNode(schema, title, card.attributes.color),
      ...(standardNodes.length > 0 ? standardNodes : [schema.node("paragraph")]),
      ...attachmentNodes(schema, card.attributes.attachments),
    ],
  )
}

/**
 * Converts a legacy card-google-classroom to a cardGoogleClassroom node: the
 * attributes map one-to-one onto the node's attrs (`_revision` → `revision`), so
 * legacyCardsFromCardStackDoc can convert back losslessly and the backend Google
 * Classroom processor keeps working off the derived legacy `cards`. Legacy embedded
 * comments/commentIds are dropped, like the other card conversions.
 */
function convertCardGoogleClassroom(schema: Schema, card: CardGoogleClassroom): PMNode {
  const attrs = card.attributes
  return schema.node("cardGoogleClassroom", {
    id: card.id,
    parentCardId: attrs.parentCardId,
    commentIds: card.attributes.commentIds,
    color: attrs.color,
    isPublic: attrs.isPublic,
    postType: attrs.postType,
    title: attrs.title,
    value: attrs.value,
    maxPoints: attrs.maxPoints,
    classSettings: attrs.classSettings,
    attachmentSettings: attrs.attachmentSettings,
    extraPostProperties: attrs.extraPostProperties,
    enabledGoogleClassIds: attrs.enabledGoogleClassIds,
    attachments: attrs.attachments,
    revision: attrs._revision,
  } satisfies ExtensionCardGoogleClassroomAttrs)
}

function blankCard(schema: Schema, title: string, color: CardColorType, isPublic: boolean): PMNode {
  return schema.node(
    "titledCard",
    {
      id: objectId(),
      parentCardId: null,
      commentIds: [],
      isPublic,
    } satisfies CardExtensionAttrs,
    [cardTitleNode(schema, title, color), schema.node("paragraph")],
  )
}

function cardTitleNode(schema: Schema, title: string, color: CardColorType): PMNode {
  return schema.node(
    "cardTitle",
    { color } satisfies ExtensionCardTitleAttrs,
    title ? schema.text(title) : undefined,
  )
}

// Replicates CcTiptap's preprocessHTML for legacy card HTML values.
function preprocessHtml(html: string): string {
  if (html.startsWith("<div data-cc-tiptap-html")) {
    return html
  }
  // CkEditor HTML: remove non-visible whitespace it adds throughout, and fix ropped-up empty paragraphs.
  html = html.replace(/[\n\t]/g, "")
  html = html.replaceAll("<p>&nbsp;</p>", "<p></p>")
  return html
}

// ----------
// legacyCardsFromCardStackDoc
// ----------

/**
 * Converts a UnifiedCardStack's ProseMirror doc to legacy cards.
 *
 * Used for reverse migrations (legacy -> unified), and to write out `cards` on edit
 * so that old code can continue reading that.
 *
 * The conversion is potentially inexact & lossy.
 * E.g., standards are serialized inside card-title-value-html's value
 * instead of becoming a card-standards.
 */
export function legacyCardsFromCardStackDoc(doc: PMNode): LessonCard[] {
  const serializer = JoinListDOMSerializer.fromSchema(doc.type.schema, "getHTML")

  const cards: LessonCard[] = []

  doc.forEach((topNode) => {
    if (topNode.type.name === "titledCard") {
      const cardAttrs = topNode.attrs as CardExtensionAttrs
      const cardTitleNode = topNode.child(0) // cardTitle is always first
      const cardTitleAttrs = cardTitleNode.attrs as ExtensionCardTitleAttrs
      const title = cardTitleNode.textContent

      const contentNodes = topNode.children.slice(1)
      const contentBlocks =
        contentNodes.length > 0 ? contentNodes : [doc.type.schema.nodes.paragraph.create()]
      cards.push({
        id: cardAttrs.id,
        type: "card-title-value-html",
        attributes: {
          title,
          value: cardBlocksToHtml(contentBlocks, serializer),
          position: null,
          color: cardTitleAttrs.color,
          attachments: [],
          commentIds: cardAttrs.commentIds,
          comments: [],
          isPublic: cardAttrs.isPublic,
          parentCardId: cardAttrs.parentCardId,
        },
      })
    } else if (topNode.type.name === "untitledCard") {
      const cardAttrs = topNode.attrs as CardExtensionAttrs

      const contentNodes = topNode.children
      const contentBlocks =
        contentNodes.length > 0 ? contentNodes : [doc.type.schema.nodes.paragraph.create()]
      cards.push({
        id: cardAttrs.id,
        type: "card-value-html",
        attributes: {
          value: cardBlocksToHtml(contentBlocks, serializer),
          position: null,
          // Unused
          color: "yellow",
          attachments: [],
          commentIds: cardAttrs.commentIds,
          comments: [],
          isPublic: cardAttrs.isPublic,
          parentCardId: cardAttrs.parentCardId,
        },
      })
    } else if (topNode.type.name === "cardGoogleClassroom") {
      cards.push(legacyCardFromGoogleClassroomNode(topNode))
    }
  })

  cards.forEach((card, i) => {
    card.attributes.position = i * 1000
  })

  return cards
}

/**
 * Converts a cardGoogleClassroom node back to a legacy card-google-classroom — the
 * exact inverse of convertCardGoogleClassroom, so the backend Google Classroom
 * processor keeps working off the derived legacy `cards`. `position` is left null
 * (callers assign it from document order).
 */
export function legacyCardFromGoogleClassroomNode(node: PMNode): CardGoogleClassroom {
  const attrs = node.attrs as ExtensionCardGoogleClassroomAttrs
  return {
    id: attrs.id,
    type: "card-google-classroom",
    attributes: {
      postType: attrs.postType,
      title: attrs.title,
      value: attrs.value,
      maxPoints: attrs.maxPoints,
      classSettings: attrs.classSettings,
      attachmentSettings: attrs.attachmentSettings,
      extraPostProperties: attrs.extraPostProperties,
      enabledGoogleClassIds: attrs.enabledGoogleClassIds,
      _revision: attrs.revision,
      position: null,
      color: attrs.color,
      attachments: attrs.attachments,
      commentIds: attrs.commentIds,
      comments: [],
      isPublic: attrs.isPublic,
      parentCardId: attrs.parentCardId,
    },
  }
}

/**
 * Serializes card-value blocks to standalone HTML: joined lists (via JoinListDOMSerializer),
 * trailing breaks, and a wrapper div that keeps the text rendering the same outside our app.
 * Also how NoteTiptap writes a note's blocks to the annotation's description.
 */
export function cardBlocksToHtml(blocks: readonly PMNode[], serializer: DOMSerializer): string {
  return pipe(
    blocks,
    (blocks) => Fragment.fromArray(blocks),
    (fragment) => addTrailingBreaks(fragment),
    (fragment) => {
      // Based on Tiptap's getHTML method.
      const documentFragment = serializer.serializeFragment(fragment)

      const temporaryDocument = document.implementation.createHTMLDocument()
      const container = temporaryDocument.createElement("div")

      container.appendChild(documentFragment)

      return container.innerHTML
    },
    (html) => {
      // Apply unified-tiptap-html class so that the HTML looks nice when rendered elsewhere in our app.
      // Also apply ProseMirror's white-space settings inline, so that the text doesn't
      // appear to change when rendered outside our app (e.g., in comment emails) -
      // taken from https://github.com/ProseMirror/prosemirror-view/blob/master/style/prosemirror.css .
      return `<div data-cc-tiptap-html class="unified-tiptap-html" style="white-space: pre-wrap; white-space: break-spaces;">${html}</div>`
    },
  )
}
